import React, { useMemo, useState } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { BASE_URL } from '../constants/config';

// Выбор лиг, где показывать спонсора: поиск, «Выбрать все»,
// галочка чемпионата отмечает все его лиги.
// props:
// groups - [{id, name, leagues: [{id, name, image, enabled}]}] (utilities/partners: leagueGroupsFrom)
// selected - id выбранных лиг
// onChange(ids) - новый список выбранных
// disabled - пока идёт сохранение

const resolveImage = (image) => {
    if (!image) return null;
    return image.startsWith('http') || image.startsWith('data:') ? image : BASE_URL.url + image;
};

// Чемпионатов много — сворачиваем те, где ничего не выбрано
const initialCollapsed = (groups, selected) => {
    if (groups.length <= 5) return {};
    const picked = new Set(selected);
    const collapsed = {};
    groups.forEach(group => {
        if (!group.leagues.some(league => picked.has(league.id))) collapsed[group.id] = true;
    });
    return collapsed;
};

export default function LeagueChecklist({ groups, selected, onChange, disabled }) {
    const [query, setQuery] = useState('');
    const [collapsed, setCollapsed] = useState(() => initialCollapsed(groups, selected));
    const selectedSet = useMemo(() => new Set(selected), [selected]);
    const q = query.trim().toLowerCase();

    // Совпал чемпионат — показываем все его лиги, иначе только совпавшие
    const visibleGroups = useMemo(() => groups
        .map(group => {
            if (!q || group.name.toLowerCase().includes(q)) return group;
            const leagues = group.leagues.filter(league => (league.name || '').toLowerCase().includes(q));
            return leagues.length ? { ...group, leagues } : null;
        })
        .filter(Boolean), [groups, q]);

    const allIds = useMemo(() => groups.flatMap(group => group.leagues.map(league => league.id)), [groups]);
    const visibleIds = visibleGroups.flatMap(group => group.leagues.map(league => league.id));
    const scopeIds = q ? visibleIds : allIds;
    const scopeChecked = scopeIds.length > 0 && scopeIds.every(id => selectedSet.has(id));
    const selectedCount = allIds.filter(id => selectedSet.has(id)).length;

    const setMany = (ids, on) => {
        const next = new Set(selected);
        ids.forEach(id => (on ? next.add(id) : next.delete(id)));
        onChange([...next]);
    };

    let scopeLabel = scopeChecked ? 'Снять все' : 'Выбрать все';
    if (q) scopeLabel = scopeChecked ? 'Снять найденные' : 'Выбрать найденные';

    return (
        <div className="ptChecklist">
            <div className="ptChecklistTools">
                <input
                    type="text"
                    className="modalInput"
                    placeholder="Найти лигу или чемпионат"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                <div className="ptChecklistBar">
                    <span className="ptChecklistCount">
                        Выбрано {selectedCount} из {allIds.length}
                    </span>
                    <button
                        type="button"
                        className="linkButton"
                        disabled={disabled || scopeIds.length === 0}
                        onClick={() => setMany(scopeIds, !scopeChecked)}
                    >
                        {scopeLabel}
                    </button>
                </div>
            </div>

            <div className="ptChecklistList">
                {groups.length === 0 && <p className="ptEmpty">У вас пока нет лиг.</p>}
                {groups.length > 0 && visibleGroups.length === 0 && <p className="ptEmpty">Ничего не найдено.</p>}
                {visibleGroups.map(group => {
                    const ids = group.leagues.map(league => league.id);
                    const count = ids.filter(id => selectedSet.has(id)).length;
                    const isOpen = q ? true : !collapsed[group.id];
                    return (
                        <div key={group.id} className="ptGroup">
                            <div className="ptGroupHead">
                                <button
                                    type="button"
                                    className="ptGroupToggle"
                                    aria-label={isOpen ? 'Свернуть' : 'Развернуть'}
                                    aria-expanded={isOpen}
                                    onClick={() => setCollapsed(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                                >
                                    {isOpen ? <FiChevronDown /> : <FiChevronRight />}
                                </button>
                                <label className="ptCheck ptGroupCheck">
                                    <input
                                        type="checkbox"
                                        checked={ids.length > 0 && count === ids.length}
                                        ref={(el) => { if (el) el.indeterminate = count > 0 && count < ids.length; }}
                                        disabled={disabled}
                                        onChange={() => setMany(ids, count !== ids.length)}
                                    />
                                    <span className="ptGroupName">{group.name}</span>
                                </label>
                                <span className="ptGroupCount">{count} из {ids.length}</span>
                            </div>
                            {isOpen && (
                                <div className="ptGroupLeagues">
                                    {group.leagues.map(league => {
                                        const image = resolveImage(league.image);
                                        return (
                                            <label key={league.id} className="ptCheck ptLeague">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedSet.has(league.id)}
                                                    disabled={disabled}
                                                    onChange={() => setMany([league.id], !selectedSet.has(league.id))}
                                                />
                                                <span className="ptLeagueLogo">
                                                    {image
                                                        ? <img src={image} alt="" loading="lazy" />
                                                        : (league.name || '?').charAt(0).toUpperCase()}
                                                </span>
                                                <span className="ptLeagueName">{league.name}</span>
                                                {Number(league.enabled) === 0 && <span className="ptTag">скрыта</span>}
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
